import { useEffect, useState } from 'react';
import { Clock, LogOut, RefreshCw } from 'lucide-react';
import { useVault } from '../lib/vault';

/**
 * Shown when the user has a keypair but hasn't been invited into the shared
 * vault data yet. Polls enrolment every 20 s so an admin's invitation
 * auto-unblocks them without a manual refresh.
 */
export function InvitationPendingScreen() {
  const user = useVault((s) => s.user);
  const refreshEnrolment = useVault((s) => s.refreshEnrolment);
  const signOut = useVault((s) => s.signOut);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function check() {
    setChecking(true);
    setError(null);
    try {
      await refreshEnrolment();
    } catch (err: any) {
      setError(err?.message ?? 'Could not check invitation status.');
    } finally {
      setChecking(false);
    }
  }

  useEffect(() => {
    const id = setInterval(check, 20_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card w-full max-w-md p-6 text-center">
        <div className="flex justify-center mb-4">
          <Clock className="text-dragon-500" size={36} />
        </div>
        <h1 className="text-lg font-semibold mb-2">Waiting for an admin to invite you</h1>
        <p className="text-sm text-ink-300 mb-4">
          Hi <strong>{user?.username}</strong>. Your Master Code is set up, so your personal key is
          ready. Before you can see the shared organisation data, an existing team member with admin
          access must grant you membership.
        </p>
        <p className="text-xs text-ink-500 mb-4">
          Ask an admin to invite <strong>{user?.username}</strong> from the Documentation app's
          Members modal. This page checks automatically every 20 seconds.
        </p>
        {error && (
          <div className="rounded border border-red-900 bg-red-950/40 p-2 text-sm text-red-300 mb-3">
            {error}
          </div>
        )}
        <button className="btn-primary w-full" onClick={check} disabled={checking}>
          <RefreshCw size={14} className={`mr-1 ${checking ? 'animate-spin' : ''}`} />
          {checking ? 'Checking…' : 'Check now'}
        </button>
        <div className="flex gap-2 mt-3">
          <button onClick={signOut} className="btn-ghost flex-1 text-xs">
            <LogOut size={14} className="mr-1" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
